import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../userEndApi/apiClient";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Instagram, Facebook, User, History, Heart, LogOut, ChevronDown, Globe } from "lucide-react";
import { useTranslate } from "../../Hooks/useTranslate";
import AuthModal from "./AuthModal";
import FormSelect from "../Custom/FormSelect";
import { useAuth } from "../../Context/AuthContext";
import { useCurrency } from "../../Context/CurrencyContext";

import RainbowDiuLogo from "./RainbowDiuLogo";

/**
 * Unified Preferences Dropdown (Currency & Language)
 */
const PreferencesDropdown = ({
  currencies,
  selectedCurrency,
  availableLanguages,
  currentLang,
  isOpen,
  setIsOpen,
  dropdownRef,
  currencyOptions,
  languageOptions,
  handleCurrencyChange,
  handleLangChange,
  customSelectStyles
}) => {

  
  const { t } = useTranslate();


  return (
    <div className="account-dropdown-wrap preferences-wrap user-select-none" ref={dropdownRef}>
      <button
        className="account-trigger d-flex align-items-center gap-1"
        onClick={() => setIsOpen(o => !o)}
        title="Settings"
      >
        <Globe size={13} />
        <span className="account-name d-none d-sm-inline">
          {selectedCurrency?.name || '...'} / {currentLang?.charAt(0).toUpperCase() + currentLang?.slice(1)}
        </span>
        <span className="account-name d-inline d-sm-none">
          {selectedCurrency?.name || '...'} / {currentLang?.substring(0, 2).toUpperCase()}
        </span>
        <ChevronDown size={13} className={`account-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="account-dropdown preferences-dropdown">
          <div className="dropdown-section mb-3">
            <h6 className="section-title mb-2">{t('currency', 'Currency')}</h6>
            <FormSelect
              field={{ name: 'currency', placeholder: t('select_currency', 'Select Currency') }}
              value={selectedCurrency?.name}
              options={currencyOptions}
              handleChange={handleCurrencyChange}
              noLabel={true}
              customStyles={customSelectStyles}
            />
          </div>

          <div className="dropdown-section">
            <h6 className="section-title mb-2">{t('language', 'Language')}</h6>
            <FormSelect
              field={{ name: 'language', placeholder: t('select_language', 'Select Language') }}
              value={currentLang}
              options={languageOptions}
              handleChange={handleLangChange}
              noLabel={true}
              customStyles={customSelectStyles}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Shows profile_picture if available and loads successfully,
 * otherwise renders initials with the site's primary gold color.
 */
const UserAvatar = ({ src, initials, size = 25 }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const style = { width: size, height: size, flexShrink: 0 };

  // Show initials if: no src, empty src, or image failed to load
  const showInitials = !src || imgFailed;

  if (!showInitials) {
    return (
      <img
        src={src}
        alt={initials}
        className="account-avatar"
        style={style}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <span
      className="user-initials-avatar"
      style={{ ...style, fontSize: Math.round(size * 0.38) }}
      aria-label={initials}
    >
      {initials}
    </span>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [menus, setMenus] = useState([]);

  const {
    currencies,
    selectedCurrency,
    setSelectedCurrency
  } = useCurrency();

  const accountRef = useRef(null);
  const currencyRef = useRef(null);

  const location = useLocation();
  const { t, currentLang, changeLang, availableLanguages } = useTranslate();
  const { user, isLoggedIn, logout } = useAuth();

  // Build display name and initials — falls back to "Renbow Resort" / "HA" if no name
  const firstName = user?.first_name?.trim() || "Guest";
  const lastName = user?.last_name?.trim() || "";
  const displayName = `${firstName} ${lastName}`;
  const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();

  const currencyOptions = (currencies || []).map(cur => ({
    value: cur.name,
    label: `(${cur.icon}) ${cur.name}`
  }));

  const languageOptions = (availableLanguages || []).map(lang => ({
    value: lang.language_name,
    label: lang.language_name.charAt(0).toUpperCase() + lang.language_name.slice(1)
  }));

  const handleCurrencyChange = (e) => {
    const selected = currencies.find(c => c.name === e.target.value);
    if (selected) setSelectedCurrency(selected);
  };

  const handleLangChange = (e) => {
    changeLang(e.target.value);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      fontSize: '12px',
      borderRadius: '8px',
      borderColor: 'rgba(0,0,0,0.1)',
      backgroundColor: '#f8f9fa',
      boxShadow: 'none',
      '&:hover': { borderColor: '$accent-gold' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: '30px' }),
    menu: (base) => ({ ...base, fontSize: '12px' })
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setIsAccountOpen(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setIsCurrencyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await apiClient.get('/homepage/get-menus');
        console.log("response",response)
        if (response.data.success) {
          const formattedMenus = response.data.data.map(menu => ({
            name: menu.menu_name.toLowerCase(),
            path: menu.menu_slug === 'home' ? '/' : `/${menu.menu_slug}`
          }));
          setMenus(formattedMenus);
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      }
    };
    fetchMenus();
  }, []);

  return (
    <>
      <nav className={`guest-navbar ${isScrolled ? "scrolled" : ""}`}>
        {/* Unique Top Bar */}
        <div className="top-bar">
          <div className="container">
            <div className="top-left d-none d-md-flex align-items-center gap-4">
              <span className="d-flex align-items-center gap-1"><Phone size={12} /> +1 (234) 567-8900</span>
              <span className="d-flex align-items-center gap-1"><Mail size={12} /> contact@Renbow Resort.com</span>
            </div>
            <div className="top-right d-flex align-items-center gap-3">
              <Link to="/"><Facebook size={14} /></Link>
              <Link to="/"><Instagram size={14} /></Link>
            </div>
          </div>
        </div>

        <div className="main-nav">
          <div className="container">
            <Link to="/" className="nav-logo">
              <RainbowDiuLogo width={40} height={40} />
              <span className="logo-text">
                Rainbow Resort
              </span>
            </Link>

            <ul className="nav-links list-unstyled mb-0">
              {menus.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className={location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path)) ? "active" : ""}
                  >
                    {t(link.name)}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="nav-cta d-flex align-items-center gap-2">
              <PreferencesDropdown
                currencies={currencies}
                selectedCurrency={selectedCurrency}
                availableLanguages={availableLanguages}
                currentLang={currentLang}
                isOpen={isCurrencyOpen}
                setIsOpen={setIsCurrencyOpen}
                dropdownRef={currencyRef}
                currencyOptions={currencyOptions}
                languageOptions={languageOptions}
                handleCurrencyChange={handleCurrencyChange}
                handleLangChange={handleLangChange}
                customSelectStyles={customSelectStyles}
              />

              {!isLoggedIn && (
                <button className="btn btn-luxury fw-bold text-uppercase" onClick={() => setIsAuthModalOpen(true)}>
                  {t('login', 'Login')}
                </button>
              )}

              {isLoggedIn && (
                <div className="account-dropdown-wrap" ref={accountRef}>
                  <button
                    className="account-trigger"
                    onClick={() => setIsAccountOpen(o => !o)}
                  >
                    <UserAvatar src={user?.profile_picture} initials={initials} size={25} />
                    <span className="account-name d-none d-lg-inline">{user?.first_name || 'Hotel'}</span>
                    <ChevronDown size={14} className={`account-chevron ${isAccountOpen ? 'open' : ''}`} />
                  </button>

                  {isAccountOpen && (
                    <div className="account-dropdown">
                      <div className="dropdown-header">
                        <UserAvatar src={user?.profile_picture} initials={initials} size={30} />
                        <div>
                          <span className="dropdown-name">{displayName}</span>
                          <span className="dropdown-email" title={user?.email}>
                            {user?.email}
                          </span>
                        </div>
                      </div>
                      <div className="dropdown-divider" />
                      <Link to="/account" className="dropdown-item" onClick={() => setIsAccountOpen(false)}>
                        <User size={15} />
                        <span>{t('my_account', 'My Account')}</span>
                      </Link>
                      <Link to="/account?tab=bookings" className="dropdown-item" onClick={() => setIsAccountOpen(false)}>
                        <History size={15} />
                        <span>{t('recent_bookings', 'Recent Bookings')}</span>
                      </Link>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item logout"
                        onClick={() => { logout(); setIsAccountOpen(false); }}
                      >
                        <LogOut size={15} />
                        <span>{t('logout', 'Sign Out')}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button className="mobile-toggle" onClick={() => setIsMenuOpen(true)}>
                <Menu size={28} />
              </button>
            </div>

          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <div className={`mobile-overlay ${isMenuOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={() => setIsMenuOpen(false)}>
          <X size={32} />
        </button>
        <ul className="mobile-links">
          {menus.map((link) => (
            <li key={link.name}>
              <Link to={link.path}>{t(link.name)}</Link>
            </li>
          ))}
        </ul>
      </div>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
};

export default Navbar;
